import React, { useMemo } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { validateField } from '../../../utils/onboardingValidation';
import { FormInput, FormSelect } from '../index';
import styles from './Step4ACHSetup.module.css';

export const Step4ACHSetup: React.FC = () => {
  const { formData, errors, updateFormField, clearError } = useOnboardingStore();

  const accountTypeOptions = useMemo(
    () => [
      { value: 'checking', label: 'Checking Account' },
      { value: 'savings', label: 'Savings Account' },
    ],
    []
  );

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    updateFormField(field, value);
    // Clear error for this field when user starts typing/changing it
    if (errors.has(field as string)) {
      clearError(field as string);
    }
  };

  const handleFieldBlur = (field: string, value: any) => {
    const validation = validateField(field, value);
    // Errors are managed by the store
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.iconBox}>
          <svg
            className={styles.icon}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2h2a1 1 0 110 2h-2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8H1a1 1 0 110-2h2V4zm2 2v8h10V6H5zm1 2h2v4H6V8zm4 0h2v4h-2V8z" />
          </svg>
        </div>
        <div>
          <h2 className={styles.title}>
            ACH Setup
          </h2>
          <p className={styles.subtitle}>
            Enter your banking information for payments
          </p>
        </div>
      </div>

      <div className={styles.formGroup}>
        <FormInput
          label="Bank Name"
          name="bankName"
          value={formData.bankName}
          onChange={(value) => handleFieldChange('bankName', value)}
          onBlur={(value) => handleFieldBlur('bankName', value)}
          placeholder="Enter your bank name"
          required
          error={errors.get('bankName')}
          helperText="Name of your financial institution"
        />

        <FormSelect
          label="Account Type"
          name="accountType"
          value={formData.accountType}
          onChange={(value) => handleFieldChange('accountType', value)}
          options={accountTypeOptions}
          placeholder="Select account type"
          required
          error={errors.get('accountType')}
        />

        <FormInput
          label="Routing Number"
          name="routingNumber"
          value={formData.routingNumber}
          onChange={(value) => handleFieldChange('routingNumber', value)}
          onBlur={(value) => handleFieldBlur('routingNumber', value)}
          placeholder="9 digit routing number"
          required
          error={errors.get('routingNumber')}
          helperText="9-digit ABA routing number"
          formatType="routingNumber"
          maxLength={9}
        />

        <FormInput
          label="Account Number"
          name="accountNumber"
          value={formData.accountNumber}
          onChange={(value) => handleFieldChange('accountNumber', value)}
          onBlur={(value) => handleFieldBlur('accountNumber', value)}
          placeholder="Enter your account number"
          required
          error={errors.get('accountNumber')}
          helperText="8-17 digit account number"
          type="password"
        />
      </div>

      {/* Information Box */}
      <div className={styles.infoBox}>
        <div className={styles.infoContent}>
          <div className={styles.infoIcon}>
            <svg
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className={styles.infoTitle}>
              Secure Banking Information
            </p>
            <p className={styles.infoText}>
              Your banking details are encrypted and securely stored. This information will be used for processing
              payments and transactions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
